/**
 * Token-baked installer scripts, generated in the browser.
 *
 * Why generate a file at all: on a fresh box the friction isn't downloading the
 * agent (nginx has always served it) — it's getting a 32-char one-time token
 * onto a machine that has no clipboard sharing with your laptop. Handing the
 * operator a script with the token already inside removes the only manual
 * transcription step, so a new PC needs neither a flash drive nor typing.
 *
 * The public /download page carries its own inline copy of these two functions
 * on purpose — it is a plain static page with zero build dependencies, so it
 * still works when the dashboard bundle doesn't. `installer-script.spec.ts`
 * extracts that copy and asserts it produces byte-identical output, so the
 * duplication cannot silently drift.
 */

/** Enroll tokens are `randomBytes(24).toString('base64url')`. */
export const TOKEN_RE = /^[A-Za-z0-9_-]{16,128}$/;
/** Device names reach a shell command line; keep them boring. */
export const DEVICE_NAME_RE = /^[A-Za-z0-9._-]{1,128}$/;

export class InstallerScriptError extends Error {}

function assertSafe(token: string, deviceName: string): void {
  // Never interpolate unvalidated text into a shell script — not even the
  // operator's own paste, which may have picked up whitespace or a stray quote.
  if (!TOKEN_RE.test(token)) throw new InstallerScriptError('invalid enroll token');
  if (deviceName && !DEVICE_NAME_RE.test(deviceName)) {
    throw new InstallerScriptError('device name must be letters, digits, dot, dash or underscore');
  }
}

/**
 * A `.cmd`, not a `.ps1`: it is the only thing that reliably runs from a
 * double-click on a stock Windows box. `.ps1` is gated by ExecutionPolicy and
 * cannot self-elevate; this can do both, scoping the policy bypass to its own
 * process so nothing about the machine is left loosened.
 */
export function windowsInstallerScript(
  token: string,
  deviceName: string,
  brokerUrl: string,
  /**
   * A customer's own PC: every screen session first shows a Yes/No box there.
   * Off by default — a planted box has nobody to ask. (Windows only: the
   * Linux child cannot prompt, so the Linux generator has no such flag.)
   */
  askBeforeScreen = false,
): string {
  assertSafe(token, deviceName);
  const nameLine = deviceName
    ? `set "DKN_NAME=${deviceName}"`
    : 'set "DKN_NAME=%COMPUTERNAME%"';
  // cmd.exe leaves `$` alone, so `$true` reaches PowerShell intact.
  const consentArg = askBeforeScreen ? ' -ScreenConsent $true' : '';
  return [
    '@echo off',
    'setlocal',
    `set "DKN_TOKEN=${token}"`,
    nameLine,
    `set "DKN_BROKER=${brokerUrl}"`,
    '',
    'rem Dikonek Tunnel agent installer. The token below is ONE-TIME and expires',
    'rem in 24 hours; delete this file once the box shows up in the dashboard.',
    '',
    'net session >nul 2>&1',
    'if errorlevel 1 (',
    '  echo Requesting administrator rights...',
    "  powershell -NoProfile -Command \"Start-Process -FilePath '%~f0' -Verb RunAs\"",
    '  exit /b',
    ')',
    '',
    'echo Installing the Dikonek Tunnel agent as "%DKN_NAME%"...',
    'powershell -NoProfile -ExecutionPolicy Bypass -Command "irm \'%DKN_BROKER%/install.ps1\' | iex;' +
      " Install-DikonekTunnel -Token '%DKN_TOKEN%' -Name '%DKN_NAME%' -BrokerUrl '%DKN_BROKER%'" +
      consentArg +
      '"',
    '',
    'echo.',
    'echo Done. This machine should now appear in the dashboard.',
    'pause',
    '',
  ].join('\r\n');
}

export function linuxInstallerScript(
  token: string,
  deviceName: string,
  brokerUrl: string,
): string {
  assertSafe(token, deviceName);
  const nameArg = deviceName ? ` --name ${deviceName}` : '';
  return [
    '#!/usr/bin/env bash',
    '# Dikonek Tunnel agent installer. The token below is ONE-TIME and expires in',
    '# 24 hours; delete this file once the box shows up in the dashboard.',
    '#   sudo bash install-dikonek-tunnel.sh',
    'set -euo pipefail',
    '[[ $EUID -eq 0 ]] || { echo "run with sudo: sudo bash $0" >&2; exit 1; }',
    `curl -fsSL ${brokerUrl}/install.sh | bash -s -- --token ${token}${nameArg}`,
    '',
  ].join('\n');
}
